import { useState, useRef } from "react";
import axios from "@/api/api";
import { API_ROUTES } from "@/api/routes";
import { normalizeTarifas } from "../utils/tarifaUtils";

/**
 * Hook personalizado para manejar la lógica de resúmenes de empresas
 */
export function useEmpresasResumen(user, sessionLoading, modo, canSeeTarifas) {
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isNocAdmin, setIsNocAdmin] = useState(false);

  const inFlightRef = useRef(new Set());
  const cacheRef = useRef(new Map());
  const bootPrefetchedRef = useRef(false);

  // Cachés para datos compartidos
  const perfilesCacheRef = useRef(null);
  const clientesCacheRef = useRef(null);

  const computeIsNocAdmin = (u) =>
    !!u?.permisos?.includes?.("noc_admin") ||
    u?.rol === "noc_admin" ||
    u?.perfil?.clave === "noc_admin" ||
    [1, 5].includes(Number(u?.perfil_id));

  // ========================= RESUMEN POR EMPRESA =========================
  async function fetchResumenEmpresa(
    empresa_id,
    { includeTarifas = false } = {}
  ) {
    // Cache simple por empresa
    if (cacheRef.current.has(empresa_id)) {
      return cacheRef.current.get(empresa_id);
    }

    const p = (async () => {
      const url =
        modo === "admin" || isNocAdmin
          ? API_ROUTES.adminFinanzas?.resumenEmpresa?.(empresa_id) ??
            API_ROUTES.resumenEmpresa(empresa_id)
          : API_ROUTES.resumenEmpresa(empresa_id);

      const { data } = await axios.get(url, { withCredentials: true });

      // ====== Lectura cruda de backend ======
      const credito_base = Number(data?.credito_base || 0);
      const credito_usado_raw = Number(data?.credito_usado || 0);
      const extraTemporal = Number(data?.extras_activos?.temporal || 0);
      const extraPromo = Number(data?.extras_activos?.promocional || 0);

      const deposito_total = Number(
        data?.deposito_total ?? data?.debito_total ?? 0
      );
      const cargos_total = Number(data?.cargos_total ?? 0);

      const hasDeposit = deposito_total > 0.0001;
      const hasCreditLimit = credito_base > 0 || extraTemporal > 0;

      // Depósito disponible (si backend lo manda, lo usamos; si no, lo calculamos)
      let depositoDisponibleRaw = Number.isFinite(
        Number(data?.balance_deposito)
      )
        ? Number(data.balance_deposito)
        : deposito_total - cargos_total;

      // Para la UI de "Depósito disponible" nunca mostramos negativo
      const depositoDisponible = Math.max(0, depositoDisponibleRaw);

      // ====== Lógica de CRÉDITO ======
      // REGLA:
      //  - Si hay límite de crédito → usamos credito_usado_raw.
      //  - Si NO hay depósito pero SÍ hay cargos → también tratamos todo como crédito (deuda).
      //  - Si hay depósito y NO hay límite de crédito → modelo sólo depósito, crédito en 0.
      let credit_used;
      let current_credit_limit;

      if (hasCreditLimit || (!hasDeposit && cargos_total > 0)) {
        // Cuenta con crédito (o deuda pura sin depósitos)
        credit_used = credito_usado_raw;
        current_credit_limit = credito_base + extraTemporal - credito_usado_raw;
      } else {
        // Solo depósito / prepago, no mostramos crédito
        credit_used = 0;
        current_credit_limit = 0;
      }

      // ====== BALANCE ======
      // Si hay depósito: balance = depositoDisponibleRaw (puede ser positivo o negativo)
      // Si NO hay depósito pero sí cargos: balance = -credit_used (deuda)
      let balance;

      if (hasDeposit) {
        // Modelo con depósito: el balance es lo que queda del depósito
        balance = depositoDisponibleRaw;
      } else if (cargos_total > 0) {
        // No hay depósito pero sí cargos: es pura deuda
        balance = -credit_used;
      } else {
        // No hay ni depósito ni cargos
        balance = 0;
      }

      // ====== Clientes para el panel de ajustes ======
      const clientes = Array.isArray(data?.clientes)
        ? data.clientes.map((c) => ({
            id: c.id ?? c.cliente_id,
            nombre:
              c.nombre ?? c.nombre_cliente ?? `Cliente ${c.id ?? c.cliente_id}`,
            deposito_asignado: Number(c.deposito_asignado || 0),
          }))
        : [];

      // Nombre de empresa
      const empresa_nombre =
        data?.empresa_nombre ||
        data?.empresa?.nombre ||
        data?.nombre_empresa ||
        null;

      // Perfil de tarifas (si backend lo manda, lo usamos)
      let perfil_nombre = data?.perfil_nombre || null;

      // Tarifas solo si se piden y hay perfil
      let tarifas = null;
      if (includeTarifas) {
        try {
          tarifas = await fetchTarifasEmpresa(empresa_id);
        } catch {
          // silencioso
        }
      }

      //======= Objeto "resumen" que consumen los componentes =======
      return {
        empresa_id,
        empresa_nombre,

        // CRÉDITO
        base_limit: credito_base,
        credit_used,
        temp_extra: extraTemporal,
        promo_extra: extraPromo,
        current_credit_limit,

        // DÉBITO
        debito_total: deposito_total,
        deposito_total,
        cargos_total,
        balance_deposito: depositoDisponible,

        // BALANCE (con signo: verde si cubierto, rojo si es deuda)
        balance,

        clientes,
        tarifas,
        perfil_nombre,
      };
    })();

    cacheRef.current.set(empresa_id, p);
    try {
      const r = await p;
      return r;
    } catch (e) {
      cacheRef.current.delete(empresa_id);
      throw e;
    }
  }

  // ========================= TARIFAS / PERFILES =========================
  async function fetchTarifasEmpresa(empresa_id) {
    const urls = [API_ROUTES?.empresaTarifas?.(empresa_id)].filter(Boolean);

    for (const url of urls) {
      try {
        const { data } = await axios.get(url, { withCredentials: true });
        const norm = normalizeTarifas(data);
        if (norm.length) return norm;
      } catch {
        // silencioso
      }
    }
    return [];
  }

  async function fetchPerfilNombre(empresa_id) {
    try {
      if (!clientesCacheRef.current) {
        const clientesUrl =
          API_ROUTES.clientesList || API_ROUTES.listadoClientes;
        const { data: clientes } = await axios.get(clientesUrl, {
          withCredentials: true,
        });
        clientesCacheRef.current = clientes;
      }

      const clientes = clientesCacheRef.current;

      const clienteEmpresa = Array.isArray(clientes)
        ? clientes.find(
            (c) => Number(c.empresa_id) === Number(empresa_id) && c.perfil_id
          )
        : null;

      if (!clienteEmpresa?.perfil_id) return null;

      if (!perfilesCacheRef.current) {
        const { data: perfiles } = await axios.get(
          API_ROUTES.perfilesTarifas || "/listado/perfiles_tarifas",
          { withCredentials: true }
        );
        perfilesCacheRef.current = perfiles;
      }

      const perfiles = perfilesCacheRef.current;

      const perfil = Array.isArray(perfiles)
        ? perfiles.find(
            (p) => Number(p.id) === Number(clienteEmpresa.perfil_id)
          )
        : null;

      return (
        perfil?.nombre ||
        perfil?.etiqueta ||
        `Perfil ${clienteEmpresa.perfil_id}`
      );
    } catch {
      return null;
    }
  }

  // ========================= UTILIDADES DE ESTADO =========================
  async function setResumenFor(empresaId, updater) {
    setEmpresas((prev) => {
      const i = prev.findIndex((x) => x.id === empresaId);
      if (i === -1) return prev;
      const cp = [...prev];
      cp[i] = updater(cp[i], i);
      return cp;
    });
  }

  async function prefetchSummaries(
    rows,
    concurrency = 3,
    includeTarifas = false
  ) {
    const queue = rows.filter(Boolean);

    const worker = async () => {
      while (queue.length) {
        const row = queue.shift();
        if (!row) continue;

        if (row.resumen || inFlightRef.current.has(row.id)) continue;

        inFlightRef.current.add(row.id);
        await setResumenFor(row.id, (r) => ({ ...r, loadingResumen: true }));

        try {
          const resumen = await fetchResumenEmpresa(row.id, { includeTarifas });
          await setResumenFor(row.id, (r) => ({
            ...r,
            resumen,
            loadingResumen: false,
          }));
        } catch {
          await setResumenFor(row.id, (r) => ({
            ...r,
            loadingResumen: false,
          }));
        } finally {
          inFlightRef.current.delete(row.id);
        }
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(concurrency, rows.length) }, worker)
    );
  }

  async function refreshEmpresaResumen(empresa_id) {
    try {
      cacheRef.current.delete(empresa_id);
      const resumen = await fetchResumenEmpresa(empresa_id, {
        includeTarifas: canSeeTarifas,
      });

      setEmpresas((prev) => {
        const i = prev.findIndex((e) => e.id === empresa_id);
        if (i === -1) return prev;
        const cp = [...prev];
        cp[i] = { ...cp[i], resumen, loadingResumen: false };
        return cp;
      });

      return resumen;
    } catch (e) {
      console.error("Error refrescando resumen:", e);
      throw e;
    }
  }

  return {
    empresas,
    setEmpresas,
    loading,
    setLoading,
    isNocAdmin,
    setIsNocAdmin,
    fetchResumenEmpresa,
    prefetchSummaries,
    refreshEmpresaResumen,
    computeIsNocAdmin,
    bootPrefetchedRef,
  };
}
